/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

/**
 * Issue Details page view (component).
 *
 */

import {Component, Vue} from 'vue-property-decorator';
import StepsList from '@/components/StepsList/index.vue';
import DetailsList from '@/components/DetailsList/index.vue';
import ImageList from '@/components/ImageList/index.vue';
import FrequencyBar from '@/components/FrequencyBar/index.vue';
import Comments from '@/components/Comments/index.vue';
import ReportIssue from '@/components/ReportIssue/index.vue';
import {
    ContributionConfigInterface, Contributions,
    IssueInterface,
    IssueTitleType,
} from '@/store/modules/issues/types';
import {Action, Getter} from 'vuex-class';
import IssueVote from '@/components/IssueVote/index.vue';
import IssueTitleInfo from '@/components/IssueTitleInfo/index.vue';
import Loading from '@/components/Loading/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import nl2br from '@/filters/Nl2Br';
import {GameInterface} from '@/store/modules/games/types';
import {AxiosResponse} from 'axios';

@Component({
    name: 'v-issue-detail',
    components: {
        'c-issue-vote': IssueVote,
        'c-issue-title-info': IssueTitleInfo,
        'c-steps-list': StepsList,
        'c-details-list': DetailsList,
        'c-image-list': ImageList,
        'c-frequency-bar': FrequencyBar,
        'c-comments': Comments,
        'c-report-issue': ReportIssue,
        'c-loading': Loading,
    },
})
export default class IssueDetail extends Vue {
    // Page Title
    @Action('updatePageTitle', {namespace: 'settingsModule'})
    public updatePageTitle!: (title: string) => void;

    // Update current view name in state
    @Action('updateCurrentView', {namespace: 'userSessionModule'})
    public updateCurrentView!: (view: {name: string, params?: any} | undefined) => void;

    // Issue
    @Action('fetchIssue', {namespace: 'issuesStateModule'}) public fetchIssue: any;
    @Action('fetchContributors', {namespace: 'issuesStateModule'})
    public fetchContributors!: (contributionConfig: ContributionConfigInterface) => Promise<void>;
    @Action('deleteIssue', {namespace: 'issuesStateModule'}) public deleteIssue!: (issueId: number) => AxiosResponse;
    @Getter('getCurrentIssue', {namespace: 'issuesStateModule'}) public issue!: IssueInterface;

    // Settings masthead
    @Action('updateMastheadImage', {namespace: 'settingsModule'})
    public updateMastheadImage!: (imageSlug: string | undefined) => void;

    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'}) public fetchGames!: () => Promise<void>;
    @Getter('getGames', {namespace: 'gamesStateModule'}) public getGames!: GameInterface[];

    public state: LoadingStateInterface = new LoadingState(false, false);
    // toDelete used in index.vue to show and hide the Delete Confirmation
    public toDelete: boolean = false;
    public isReadOnly: boolean = false;
    public displayReproductionSteps: string|null = '';

    /**
     * @return {number}
     */
    get titleType(): number {
        return IssueTitleType.TITLE;
    }

    /**
     * Mounted lifecycle hook
     */
    public async mounted(): Promise<void>  {
        // Update current view name in state
        this.updateCurrentView({
            name: this.$router.currentRoute.name || '/',
            params: this.$router.currentRoute.params,
        });

        try {
            this.state.loading = true;
            this.state.error = false;
            // Set the page title
            this.updatePageTitle(this.$i18n.translate('menu.header.issueDetails', {}) || '');
            await this.fetchIssue(+this.$route.params.id);
            await this.fetchContributors({
                issueId: +this.$route.params.id,
                itemsPerPage: Contributions.ITEMS_PER_PAGE,
            });

            // Update the masthead image - must get the key_art from the games
            await this.fetchGames();
            const game: GameInterface = this.getGames.find((gameItem: GameInterface): boolean => {
                return this.issue.game === gameItem.name;
            }) as GameInterface;
            const keyArt: string|undefined = this.getKeyArt(game);
            this.updateMastheadImage(keyArt);

            for (const eachGame of this.getGames) {
                if (typeof game !== 'undefined' && game.name === eachGame.name && eachGame.readonly === true) {
                    this.isReadOnly = true;
                }
            }
            const steps: HTMLElement = document.createElement('stepsToReproduce');
            if (this.issue.issueDetails) {
                steps.innerHTML = this.issue.issueDetails.stepsToReproduce;
                this.displayReproductionSteps = steps.childNodes[0].nodeValue;
            }
        } catch (e) {
            this.state.error = true;
        } finally {
            this.state.loading = false;
        }
    }

    /**
     * Destroyed lifecycle hook
     */
    public destroyed(): void {
        this.updateMastheadImage(undefined);
    }

    /**
     * Go to Edit page
     */
    public goToEdit(): void {
        this.$router.push({name: 'edit-issue', params: {id: JSON.stringify(this.issue.id)}});
    }

    /**
     * Delete Issue
     */
     public async deleteMyIssue(): Promise<void> {
         try {
             await this.deleteIssue(this.issue.id);
             this.$toastr.success(this.$i18n.translate('view.issueDetails.issue.delete.success', {}) || '');
             this.$router.push({name: 'my-issues'});
         } catch (e) {
             this.$toastr.error(this.$i18n.translate('view.issueDetails.issue.delete.error', {}) || '');
         }
    }

    private getKeyArt(game: GameInterface|undefined): string|undefined {
        if (game !== undefined && game.branding !== null && game.branding.key_art !== null) {
            return game.branding.key_art.url;
        }
        return undefined;
    }
}
